package gr.tech.pdfgenerator.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.browser.dom.ElementTarget
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.overlay.PopupPlacement
import com.varabyte.kobweb.silk.components.overlay.Tooltip
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorPalettes
import gr.tech.pdfgenerator.components.TabView
import gr.tech.pdfgenerator.components.layouts.AuthPageLayout
import gr.tech.pdfgenerator.components.widgets.*
import gr.tech.pdfgenerator.infrastructure.api.deleteProjectByID
import gr.tech.pdfgenerator.infrastructure.api.fetchProjectByID
import gr.tech.pdfgenerator.infrastructure.api.logoutUser
import gr.tech.pdfgenerator.infrastructure.api.parseProjectApiErrorResponse
import gr.tech.pdfgenerator.infrastructure.api.projectSaveToDatabase
import gr.tech.pdfgenerator.infrastructure.data.GeneratorData
import gr.tech.pdfgenerator.infrastructure.session.UserSession
import gr.tech.pdfgenerator.models.user.USER_ROLES
import gr.tech.pdfgenerator.navigation.SitePage
import gr.tech.pdfgenerator.sections.*
import gr.tech.pdfgenerator.styles.GeneralButtonStyle
import gr.tech.pdfgenerator.styles.HomePageStyle
import gr.tech.pdfgenerator.styles.Theme
import gr.tech.pdfgenerator.util.CONSTANTS.FONT_FAMILY2
import gr.tech.pdfgenerator.util.StateAppendix
import gr.tech.pdfgenerator.util.BACKEND
import gr.tech.pdfgenerator.util.formatDateAndTime
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@Page(routeOverride = "inputs")
@Composable
fun InputPage() {
    AuthPageLayout(
        title = "Inputs",
        requiredRoles = listOf(USER_ROLES.CONFIRMED_USER.role)
    ) {
        InputsScreen()
    }
}

data class HomePageUIState(
    val showDeleteConfirmation: Boolean = false,
    val showSaveConfirmation: Boolean = false,
    val showDetailsModal: Boolean = false,

    /* Save before leaving flags/options */
    val showSaveFirstConfirmation: Boolean = false,
    val showSaveFirstRoute: String = "",
    val useKobwebRouter: Boolean = false,

    val isLoading: Boolean = true,

    var apiErrorCode: String = "",
    var errorText: String = "",
    var projectNotFound: Boolean = false,

    val projectId: String? = null,
    val lastSave: String? = null,
)

@Composable
fun InputsScreen() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val stateAppendix = StateAppendix("AverageElectricityRatesRebatePro.csv", scope)
    var pageState by remember { mutableStateOf(HomePageUIState()) }

    LeaveWarningHandler(GeneratorData.hasUnsavedChanges())
    //BackButtonGuard(GeneratorData.hasUnsavedChanges()) //Need to rework, notice cmes up but still goes through.

    LaunchedEffect(Unit) {
        pageState = pageState.copy(
            projectId = ctx.route.params["projectid"]
        )

        if (pageState.projectId == null) {
            GeneratorData.unloadProject()
        } else {
            fetchProjectByID(
                id = pageState.projectId!!,
                onApiResponse = { fetchedProject ->
                    GeneratorData.loadProjectFromDB(
                        loadingProject = fetchedProject
                    )
                },
                onError = { errorCode ->
                    pageState = pageState.copy(
                        apiErrorCode = errorCode,
                        errorText = parseProjectApiErrorResponse(errorCode),
                        projectNotFound = errorCode == "no_project"
                    )
                }
            )
        }
        document.title = GeneratorData.project.value.projectTitle ?: "Untitled Project"

        pageState = pageState.copy(
            isLoading = false
        )
    }

    if (pageState.showSaveConfirmation) {
        ConfirmModal(
            actionText = "Save Project",
            subText = if (pageState.projectId == null)
                "Save New Project?" else "Save and Update Project?",
            onConfirm = {
                pageState = pageState.copy(
                    isLoading = true
                )
                scope.launch {
                    GeneratorData.saveProject()
                    val response = projectSaveToDatabase(
                        id = GeneratorData.project.value.id,
                        project = GeneratorData.project.value
                    )
                    if (response.success && response.data != null) {
                        var currentPath = window.location.href
                        if (response.data.id != null && pageState.projectId == null) {
                            // If new project, we add the new ID to the URL before refreshing.
                            currentPath += "?projectid=${response.data.id}"
                        }
                        // Refresh, with new ID and just have it pull the information.
                        // Got rid of the previous way of receiving the updated row, and loading that new data.
                        // Feel like it got buggy.
                        ctx.router.navigateTo(currentPath)
                    }
                    pageState = pageState.copy(
                        showSaveConfirmation = false
                    )
                }
            },
            onDismiss = {
                pageState = pageState.copy(
                    showSaveConfirmation = false
                )
            }
        )
    }

    if (pageState.showDeleteConfirmation) {
        ConfirmModal(
            actionText = "Delete Project",
            onConfirm = {
                scope.launch {
                    if (GeneratorData.project.value.id != null) {
                        // Is a current DB saved project, delete with API call.
                        val response = deleteProjectByID(
                            id = GeneratorData.project.value.id!!,
                            onSuccess = {
                                ctx.router.tryRoutingTo("/projects")
                            },
                            onError = {
                                pageState.errorText = it
                            }
                        )
                    } else {
                        // Is new project, unload all data and return to project selection.
                        ctx.router.tryRoutingTo("/projects")
                    }

                    pageState = pageState.copy(
                        showDeleteConfirmation = false
                    )
                }
            },
            onDismiss = {
                pageState = pageState.copy(
                    showDeleteConfirmation = false
                )
            }
        )
    }

    if (pageState.showSaveFirstConfirmation) {
        SaveBeforeLeavingModal(
            context = ctx,
            scope = scope,
            kobwebRouter = pageState.useKobwebRouter,
            path = pageState.showSaveFirstRoute,
            onDismiss = {
                pageState = pageState.copy(
                    showSaveFirstConfirmation = false
                )
            }
        )
    }

    if (pageState.showDetailsModal) {
        ProjectDetailsModal(
            scope = scope,
            onDismiss = {
                pageState = pageState.copy(
                    showDetailsModal = false
                )
            }
        )
    }

    /* PAGE LAYOUT */
    if (!pageState.isLoading && pageState.errorText.isBlank()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
        ) {

            // Side Column for the project buttons
            Column(
                modifier = Modifier
                    .width(195.px)
                    .fillMaxHeight()
                    .borderRight(1.px, LineStyle.Solid, Colors.LightGrey)
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(100.lvh)
                        .top(0.px)
                        .position(Position.Sticky)
                        .padding(leftRight = 12.px, top = 12.px, bottom = 24.px)
                ) {
                    H1(
                        attrs = Modifier
                            .fillMaxWidth()
                            .fontSize(1.75.cssRem)
                            .classNames("title")
                            .textAlign(TextAlign.Center)
                            .toAttrs()
                    ) {
                        Text("PDF Genie")
                    }

                    Button(
                        size = ButtonSize.LG,
                        modifier = GeneralButtonStyle.toModifier()
                            .fillMaxWidth()
                            .border(2.px, LineStyle.Solid, Theme.MediumGreen.rgb)
                            .color(Theme.MediumGreen.rgb)
                            .backgroundColor(Colors.White),
                        onClick = {
                            if (GeneratorData.hasUnsavedChanges()) {
                                pageState = pageState.copy(
                                    showSaveFirstConfirmation = true,
                                    showSaveFirstRoute = SitePage.Projects.route,
                                    useKobwebRouter = true
                                )
                            } else {
                                ctx.router.tryRoutingTo(SitePage.Projects.route)
                            }
                        }
                    ) {
                        FaSquareCaretLeft(
                            size = IconSize.SM,
                            modifier = Modifier.margin(right = 6.px).color(Theme.MediumGreen.rgb),
                        )
                        Text("View Projects")
                    }

                    HorizontalDivider(
                        modifier = Modifier
                            .margin(topBottom = 16.px)
                    )

                    // Details & Trash Buttons
                    Row(
                        modifier = Modifier
                            .margin(bottom = 8.px)
                            .gap(6.px)
                            .fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Button(
                            size = ButtonSize.LG,
                            modifier = GeneralButtonStyle.toModifier()
                                .fillMaxWidth()
                                .border(2.px, LineStyle.Solid, Theme.MediumGreen.rgb)
                                .backgroundColor(Colors.White)
                                .color(Colors.White),
                            onClick = {
                                pageState = pageState.copy(
                                    showDetailsModal = true
                                )
                            }
                        ) {
                            FaCircleInfo(size = IconSize.SM, modifier = Modifier.color(Theme.MediumGreen.rgb))
                        }

                        Button(
                            size = ButtonSize.LG,
                            modifier = GeneralButtonStyle.toModifier()
                                .border(2.px, LineStyle.Solid, Theme.MediumGreen.rgb)
                                .backgroundColor(Colors.White)
                                .fillMaxWidth()
                                .color(Colors.White),
                            onClick = {
                                pageState = pageState.copy(
                                    showDeleteConfirmation = true
                                )
                            }
                        ) {
                            FaTrash(size = IconSize.SM, modifier = Modifier.color(Theme.MediumGreen.rgb))
                        }
                    }

                    Button(
                        size = ButtonSize.LG,
                        modifier = GeneralButtonStyle.toModifier()
                            .fillMaxWidth()
                            .margin(bottom = 4.px)
                            .backgroundColor(Theme.MediumGreen.rgb)
                            .color(Colors.White),
                        onClick = {
                            pageState = pageState.copy(
                                showSaveConfirmation = true
                            )
                        }
                    ) {
                        FaFloppyDisk(
                            size = IconSize.SM,
                            modifier = Modifier.margin(right = 6.px).color(Colors.White),
                            style = IconStyle.FILLED
                        )
                        Text("Save Project")
                    }

                    P(
                        attrs = Modifier
                            .margin(leftRight = autoLength, bottom = 8.px)
                            .fontFamily(FONT_FAMILY2)
                            .fontWeight(FontWeight.Light)
                            .textAlign(TextAlign.Center)
                            .borderBottom(1.px, LineStyle.Dashed, Colors.DarkGray)
                            .cursor(Cursor.Help)
                            .toAttrs()
                    ) {
                        if (GeneratorData.hasUnsavedChanges()) {
                            FaXmark(size = IconSize.LG, modifier = Modifier.margin(right = 6.px).color(Colors.Red))
                            SpanText(
                                text = "Save changes!"
                            )
                        } else {
                            FaCheck(
                                size = IconSize.LG,
                                modifier = Modifier.margin(right = 6.px).color(Colors.Green)
                            )
                            SpanText(
                                text = "Up to Date!"
                            )
                        }
                        Tooltip(
                            target = ElementTarget.PreviousSibling,
                            modifier = Modifier
                                .color(Colors.White)
                                .backgroundColor(Theme.DarkGreen.rgb)
                                .padding(6.px),
                            placement = PopupPlacement.Right,
                            showDelayMs = 50
                        ) {
                            val lastSaved = formatDateAndTime(GeneratorData.project.value.updatedAt)
                            if (lastSaved != null) {
                                Text("Last Save: $lastSaved")
                            } else {
                                Text("Not Saved!")
                            }
                        }
                    }

                    Button(
                        enabled = GeneratorData.selectedTabIndex.value >= 0,
                        size = ButtonSize.LG,
                        modifier = GeneralButtonStyle.toModifier()
                            .backgroundColor(Theme.DarkGreen.rgb)
                            .fillMaxWidth()
                            .margin(bottom = 16.px)
                            .color(Colors.White),
                        onClick = {
                            scope.launch {
                                pageState = pageState.copy(
                                    isLoading = true
                                )
                                GeneratorData.saveProject()
                                val response = projectSaveToDatabase(
                                    id = GeneratorData.project.value.id,
                                    project = GeneratorData.project.value
                                )
                                if (response.success && response.data != null) {
                                    GeneratorData.loadProjectFromDB(response.data)
                                    ctx.router.navigateTo(
                                        pathQueryAndFragment = GeneratorData.selectedTabName.value.lowercase().replace(" ", ""),
                                        openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                                    )
                                    pageState = pageState.copy(
                                        isLoading = false
                                    )
                                } else {
                                    console.error("Error Saving Project to DB!")
                                }
                            }
                        }
                    ) {
                        FaFilePdf(
                            size = IconSize.SM,
                            modifier = Modifier.margin(right = 6.px).color(Colors.White),
                            style = IconStyle.FILLED
                        )
                        Text("Generate")
                    }

                    Spacer()
                    SpanText(
                        text = "Hello, ${UserSession.info.value!!.name}!",
                        modifier = Modifier
                            .fillMaxWidth()
                            .textAlign(TextAlign.Center)
                            .margin(bottom = 8.px)
                    )
                    Button(
                        size = ButtonSize.LG,
                        modifier = GeneralButtonStyle.toModifier()
                            .fillMaxWidth()
                            .border(2.px, LineStyle.Solid, Theme.MediumGreen.rgb)
                            .margin(0.px)
                            .color(Theme.MediumGreen.rgb)
                            .backgroundColor(Colors.White),
                        onClick = {
                            if (GeneratorData.hasUnsavedChanges()) {
                                pageState = pageState.copy(
                                    showSaveFirstConfirmation = true,
                                    showSaveFirstRoute = "${BACKEND.BACKEND_URL}${BACKEND.ROUTES.USER_SIGN_OUT}",
                                    useKobwebRouter = false,
                                )
                            }
                            scope.launch { logoutUser() }
                        }
                    ) {
                        FaRightToBracket(
                            size = IconSize.SM,
                            modifier = Modifier.margin(right = 6.px).color(Theme.MediumGreen.rgb),
                        )
                        Text("Sign Out")
                    }
                }
            }


            // Inputs Column, the majority of the screen.
            Column(
                modifier = HomePageStyle.toModifier()
                    .width(1313.px)
            ) {

                H2(
                    attrs = Modifier
                        .fontFamily(FONT_FAMILY2)
                        .fontSize(1.4.cssRem)
                        .margin(
                            bottom = 8.px
                        )
                        .toAttrs()
                ) {
                    Text("Project Details")
                }

                projectDetailsInputs()

                H2(
                    attrs = Modifier
                        .fontFamily(FONT_FAMILY2)
                        .fontSize(1.4.cssRem)
                        .margin(
                            bottom = 8.px
                        )
                        .toAttrs()
                ) {
                    Text("General Inputs")
                }

                generalInputsSection()

                H2(
                    attrs = Modifier
                        .fontFamily(FONT_FAMILY2)
                        .fontSize(1.4.cssRem)
                        .margin(top = 16.px)
                        .toAttrs()
                ) {
                    Text("Specific PDF Inputs")
                }

                SpanText(
                    text = "(Select tab, fill inputs, click Generate)",
                    modifier = Modifier
                        .fontSize(1.cssRem)
                        .fontWeight(FontWeight.Light)
                        .fontFamily(FONT_FAMILY2)
                        .margin(bottom = 8.px)
                )

                val tabsList = listOf("MSA", "Lighting Proposal", "Tier Three")
                TabView(
                    tabNamesList = tabsList,
                    tabComposableList = listOf(
                        { msaInputsSection() },
                        { proposalInputsSection(stateAppendix) },
                        { tierThreeInputsSection() }
                    )
                )
            }
        }

    }
    else if (!pageState.isLoading && pageState.errorText.isNotBlank()) {
        // Error loading the page
        Box(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.Center
        ) {
            Column(
                modifier = Modifier
                    .width(375.px),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                P(
                    attrs = Modifier
                        .fontFamily(FONT_FAMILY2)
                        .fontSize(1.5.cssRem)
                        .fontWeight(FontWeight.Bold)
                        .margin(bottom = 6.px)
                        .toAttrs()
                ) {
                    Text(pageState.errorText)
                }
                Button(
                    colorPalette = ColorPalettes.Green,
                    size = ButtonSize.LG,
                    modifier = Modifier
                        .color(Colors.White),
                    onClick = {
                        ctx.router.tryRoutingTo("/projects")
                    }
                ) {
                    FaCircleArrowLeft(
                        size = IconSize.SM,
                        modifier = Modifier.margin(right = 6.px).color(Colors.White),
                    )
                    Text("Return to Projects")
                }

            }
        }

    }
    else {
        // Loading state (can swap with skeleton if needed)
        Box(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.Center
        ) {
            loadingSpinner(iconSize = IconSize.X5)
        }
    }
}
